import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function HomePage() {
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [subscribeMsg, setSubscribeMsg] = useState('');

  const hotComics = comics.filter((c) => c.isHot).slice(0, 6);
  const newComics = comics.filter((c) => c.isNew).slice(0, 6);
  const latestComics = [...comics].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()).slice(0, 8);

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setSubscribeStatus('success');
      setSubscribeMsg('感谢订阅！我们会将最新韩漫资讯发送到您的邮箱。');
      setEmail('');
      return;
    }
    const formData = new FormData(form);
    try {
      const res = await fetch('https://readdy.ai/api/form/d9p0upqqs38sj6jc7ha0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setSubscribeStatus('success');
        setSubscribeMsg('感谢订阅！我们会将最新韩漫资讯发送到您的邮箱。');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '订阅失败，请稍后重试';
        setSubscribeStatus('error');
        setSubscribeMsg(serverMsg);
      }
    } catch {
      setSubscribeStatus('error');
      setSubscribeMsg('网络错误，请稍后重试');
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '韩漫网',
    url: 'https://hanmanwang.com',
    description: '韩漫网提供海量免费韩国漫画在线阅读，精选热门韩漫、最新韩漫新番，涵盖恋爱、奇幻、热血等多种类型。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://hanmanwang.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[520px] md:h-[620px] flex items-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20art%20style%20illustration%20background%2C%20beautiful%20anime%20style%20scenery%20with%20warm%20sunset%20colors%2C%20magical%20atmosphere%2C%20soft%20gradients%2C%20dreamy%20manga%20aesthetic%2C%20ethereal%20lighting%2C%20whimsical%20fantasy%20world%2C%20pastel%20color%20palette%2C%20clean%20composition&width=1800&height=800&seq=hero-bg-hanman&orientation=landscape"
            alt="韩漫网 - 免费韩国漫画在线阅读"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="relative w-full px-4 md:px-6 z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 font-heading leading-tight">
                探索精彩的<strong className="text-primary-400 font-bold">韩漫世界</strong>
              </h1>
              <p className="text-base md:text-lg text-background-200/90 mb-8 max-w-xl mx-auto leading-relaxed">
                海量免费韩国漫画在线阅读，精选热门韩漫、最新韩漫新番，高清画质，流畅阅读体验，随时随地畅享韩漫乐趣
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/love"
                  className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-all duration-200 text-sm whitespace-nowrap"
                >
                  开始阅读
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-3 bg-background-50/15 hover:bg-background-50/25 text-background-50 font-medium rounded-lg border border-background-50/30 transition-all duration-200 text-sm whitespace-nowrap backdrop-blur-sm"
                >
                  了解更多
                </Link>
              </div>
              <div className="flex items-center justify-center gap-6 mt-10">
                {[
                  { value: '10,000+', label: '漫画作品' },
                  { value: '500万+', label: '月活读者' },
                  { value: '98%', label: '好评率' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="text-2xl md:text-3xl font-bold text-background-50 font-heading">{stat.value}</div>
                    <div className="text-xs text-background-300 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Hot Comics Section */}
        <section className="py-14 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                  热门<strong className="text-primary-500 font-bold">韩漫</strong>
                </h2>
                <p className="text-sm text-foreground-500 mt-1">千万读者正在追的韩漫佳作</p>
              </div>
              <Link
                to="/category/love"
                className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="true">
              {hotComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Category Section */}
        <section className="py-14 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-[1280px] mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                漫画<strong className="text-primary-500 font-bold">分类</strong>
              </h2>
              <p className="text-sm text-foreground-500 mt-1">按喜好找到最适合你的韩漫类型</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-3 p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <div className="text-center">
                    <h3 className="text-sm font-semibold text-foreground-900 font-heading whitespace-nowrap">{cat.name}</h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{cat.count}部作品</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* New Comics Section */}
        <section className="py-14 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                  最新<strong className="text-accent-500 font-bold">韩漫新番</strong>
                </h2>
                <p className="text-sm text-foreground-500 mt-1">刚刚上线的热门新作，别错过</p>
              </div>
              <Link
                to="/category/fantasy"
                className="flex items-center gap-1 text-sm font-medium text-accent-600 hover:text-accent-700 transition-colors whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="true">
              {newComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-14 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-[1280px] mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-8 text-center">
              最近<strong className="text-primary-500 font-bold">更新</strong>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {latestComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="flex gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-200 group"
                >
                  <div className="w-16 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                    <img src={comic.coverImage} alt={comic.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground-900 truncate font-heading group-hover:text-primary-600 transition-colors">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
                    <div className="flex items-center gap-1 mt-1.5">
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-secondary-100 text-secondary-700 rounded whitespace-nowrap">
                        {comic.category}
                      </span>
                    </div>
                    <p className="text-[11px] text-foreground-400 mt-1.5">
                      更新至第{comic.chapters.length}话 · {comic.updatedAt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter */}
        <section className="py-14 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-[640px] mx-auto text-center">
            <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-5">
              <i className="ri-mail-send-line text-2xl"></i>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
              订阅<strong className="text-primary-500 font-bold">韩漫资讯</strong>
            </h2>
            <p className="text-sm text-foreground-500 mb-6">
              第一时间获取最新韩漫更新、热门推荐和独家福利
            </p>
            <form
              onSubmit={handleSubscribe}
              data-readdy-form="true"
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="请输入您的邮箱"
                required
                className="flex-1 px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
              />
              <button
                type="submit"
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                立即订阅
              </button>
            </form>
            {subscribeStatus !== 'idle' && (
              <p className={`mt-3 text-sm ${subscribeStatus === 'success' ? 'text-accent-600' : 'text-red-500'}`}>
                {subscribeMsg}
              </p>
            )}
            <p className="text-xs text-foreground-400 mt-4">我们尊重您的隐私，不会发送垃圾邮件</p>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}